"use client";

import { useEffect, useMemo, useState } from "react";

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  headings: Heading[];
}

export default function TableOfContents({ headings }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("");

  // Ensure heading ids are unique to avoid duplicate keys/anchors
  const uniqueHeadings = useMemo(() => {
    const counts = new Map<string, number>();
    return headings.map((heading) => {
      const count = counts.get(heading.id) || 0;
      counts.set(heading.id, count + 1);
      const uniqueId = count === 0 ? heading.id : `${heading.id}-${count}`;
      return { ...heading, id: uniqueId };
    });
  }, [headings]);

  useEffect(() => {
    if (uniqueHeadings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-20% 0% -35% 0%",
        threshold: 0,
      }
    );

    // Observe all headings
    uniqueHeadings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        // Ensure DOM id matches unique id
        if (element.id !== heading.id) {
          element.id = heading.id;
        }
        observer.observe(element);
      }
    });

    // Set the first heading as active initially if none is intersecting
    if (!activeId && uniqueHeadings.length > 0) {
      setActiveId(uniqueHeadings[0].id);
    }

    return () => {
      uniqueHeadings.forEach((heading) => {
        const element = document.getElementById(heading.id);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [uniqueHeadings, activeId]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Offset for sticky header/navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  if (uniqueHeadings.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="rounded-lg bg-white p-4">
        <h3 className="mb-4 text-hims-body font-semibold text-hims-dark-gray">
          Contents
        </h3>
        <nav className="space-y-1">
          {uniqueHeadings.map((heading) => {
            const isActive = activeId === heading.id;
            const isH2 = heading.level === 2;

            return (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(heading.id);
                }}
                className={`block rounded px-2 py-1.5 text-left text-xs transition-colors ${
                  isActive
                    ? "bg-hims-beige-bg font-semibold text-hims-dark-gray"
                    : isH2
                      ? "text-black hover:bg-hims-beige-bg"
                      : "text-hims-medium-gray hover:bg-hims-beige-bg hover:text-hims-dark-gray"
                }`}
              >
                {heading.text}
              </a>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

